import { Controller } from "@hotwired/stimulus"
import AirDatepicker from "air-datepicker"
import localeUK from "air-datepicker/locale/en"

export default class extends Controller {
  static targets = ["calendar", "calendarMount", "calendarTrigger"]

  static values = {
    from: String,
    to: String,
    url: String,
    frame: String,
    holidays: Array,
    maxDays: Number
  }

  connect() {
    this.holidays = new Set(this.holidaysValue)
  }

  disconnect() {
    this.picker?.destroy()
    this.picker = null
  }

  toggleCalendar() {
    if (this.calendarTarget.classList.contains("hidden")) {
      this.openCalendar()
    } else {
      this.closeCalendar()
    }
  }

  closeOnOutside(event) {
    if (this.calendarTarget.classList.contains("hidden")) return
    if (this.calendarTarget.contains(event.target) || this.calendarTriggerTarget.contains(event.target)) return

    this.closeCalendar()
  }

  openCalendar() {
    if (!this.picker) {
      this.picker = new AirDatepicker(this.calendarMountTarget, {
        inline: true,
        range: true,
        toggleSelected: false,
        locale: localeUK,
        firstDay: 1,
        navTitles: { days: "MMMM yyyy" },
        startDate: this.parseDate(this.fromValue),
        onRenderCell: ({ date, cellType, datepicker }) => this.renderCell(date, cellType, datepicker),
        onSelect: ({ date }) => this.select(date)
      })
    }

    this.calendarTarget.classList.remove("hidden")
    this.calendarTriggerTarget.setAttribute("aria-expanded", "true")
  }

  closeCalendar() {
    if (this.calendarTarget.classList.contains("hidden")) return

    this.calendarTarget.classList.add("hidden")
    this.calendarTriggerTarget.setAttribute("aria-expanded", "false")

    if (this.picker?.selectedDates.length === 1) {
      this.picker.clear({ silent: true })
      this.picker.update()
    }
  }

  renderCell(date, cellType, datepicker) {
    if (cellType !== "day") return undefined

    const picked = datepicker.selectedDates
    if (picked.length === 1) {
      return { disabled: this.workingDaysBetween(picked[0], date) > this.maxDaysValue }
    }

    return { disabled: false, classes: this.periodClasses(date) }
  }

  select(dates) {
    if (dates.length === 1) {
      this.picker.update()
      return
    }
    if (dates.length !== 2) return

    const [from, to] = dates
    this.closeCalendar()
    Turbo.visit(`${this.urlValue}?from=${this.iso(from)}&to=${this.iso(to)}`, {
      frame: this.frameValue,
      action: "advance"
    })
  }

  periodClasses(date) {
    const iso = this.iso(date)
    if (iso < this.fromValue || iso > this.toValue) return ""

    const classes = []
    if (iso === this.fromValue) classes.push("-range-from-")
    if (iso === this.toValue) classes.push("-range-to-")
    if (iso > this.fromValue && iso < this.toValue) classes.push("-in-range-")

    return classes.join(" ")
  }

  workingDaysBetween(first, second) {
    const [start, end] = first < second ? [first, second] : [second, first]
    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate())
    const limit = new Date(end.getFullYear(), end.getMonth(), end.getDate())
    let count = 0

    while (cursor <= limit && count <= this.maxDaysValue) {
      if (this.workingDay(cursor)) count += 1
      cursor.setDate(cursor.getDate() + 1)
    }

    return count
  }

  workingDay(date) {
    const day = date.getDay()
    return day !== 0 && day !== 6 && !this.holidays.has(this.iso(date))
  }

  parseDate(value) {
    const [year, month, day] = value.split("-").map(Number)
    return new Date(year, month - 1, day)
  }

  iso(date) {
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${date.getFullYear()}-${month}-${day}`
  }
}
